import { CampaignDetails } from "@/lib/types";
import { selectActiveFirm } from "@/store/appSelectors";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { LoadingSmall } from "@/components/LoadingSmall";
import React, { useState } from "react";
import { constructCampaignSchedule, PlayerSchedule } from "@/pages/player/playerDetail/PlayerScheduleDetailUtils";
import { ScheduleSection } from "@/pages/player/playerDetail/ScheduleSection";
import { PlayerTimezoneSelect } from "@/pages/player/form/PlayerTimezoneSelect";
import { Button } from "@/components/ui/button";

interface Props {
  campaignDetails: CampaignDetails;
}

export const CampaignSchedulePreview: React.FC<Props> = ({ campaignDetails }) => {
  const { t } = useTranslation();
  const activeFirm = useSelector(selectActiveFirm);
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [showPreview, setShowPreview] = useState(false);

  const { data: campaignSchedule, isFetching } = useQuery({
    queryFn: () => constructCampaignSchedule(campaignDetails.id, String(activeFirm?.firmGuid), timezone),
    queryKey: ["campaignSchedule", timezone],
    enabled: showPreview,
  });

  if (!showPreview) {
    return (
      <div className="space-y-4">
        <Button onClick={() => setShowPreview(true)} variant="outline">
          {t("Preview")}
        </Button>
      </div>
    );
  }

  if (isFetching) {
    return <LoadingSmall />;
  }

  if (!campaignSchedule) {
    return <div className="text-sm text-muted-foreground">{t("schedule.noSchedule")}</div>;
  }

  const s: PlayerSchedule = {
    campaignConfs: [campaignSchedule],
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button onClick={() => setShowPreview(false)} variant="outline" size="sm">
          {t("Hide")}
        </Button>
        {/* @ts-ignore */}
        <PlayerTimezoneSelect field={{ value: timezone, onChange: setTimezone }} />
      </div>
      {s.campaignConfs?.map((conf, index) => (
        <ScheduleSection key={index} conf={conf} type="campaign" isOverridenByArea={s.isOverridenByArea} />
      ))}
    </div>
  );
};
